import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';

type View = 'menu' | 'cashier' | 'admin';

function translateAdminError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('invalid login credentials')) {
    return 'Correo o contraseña incorrectos.';
  }
  if (lower.includes('email not confirmed')) {
    return 'Debes confirmar tu correo antes de iniciar sesión.';
  }
  if (lower.includes('rate limit') || lower.includes('too many')) {
    return 'Demasiados intentos. Espera un momento e inténtalo de nuevo.';
  }
  return 'No se pudo iniciar sesión. Revisa tus datos e inténtalo de nuevo.';
}

function mapCashierError(message: string): string {
  const m = message.toLowerCase();
  if (m.includes('incorrecto')) return 'Código incorrecto';
  if (m.includes('desactivad')) return 'Este cajero está desactivado. Contacta al administrador.';
  if (m.includes('inactiva')) return 'La sucursal asignada está inactiva. Contacta al administrador.';
  if (m.includes('no tiene una sucursal')) return 'Este cajero no tiene una sucursal asignada. Contacta al administrador.';
  if (m.includes('intentos')) return 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.';
  if (m.includes('dígito') || m.includes('digito')) return 'Ingresa tu código de 6 dígitos.';
  return 'No se pudo validar el acceso. Comprueba tu conexión e inténtalo nuevamente.';
}

const PARTICLES = [
  { left: '8%', top: '18%', size: 3, delay: 0, dur: 18, cls: '' },
  { left: '15%', top: '72%', size: 2, delay: 2, dur: 22, cls: 'hidden-md' },
  { left: '22%', top: '30%', size: 4, delay: 4, dur: 26, cls: '' },
  { left: '78%', top: '22%', size: 3, delay: 1, dur: 20, cls: 'hidden-md' },
  { left: '88%', top: '65%', size: 2, delay: 3, dur: 24, cls: 'hidden-sm' },
  { left: '70%', top: '82%', size: 4, delay: 5, dur: 28, cls: 'hidden-sm' },
  { left: '35%', top: '88%', size: 2, delay: 1.5, dur: 19, cls: 'hidden-md' },
  { left: '60%', top: '12%', size: 3, delay: 2.5, dur: 21, cls: '' },
  { left: '90%', top: '40%', size: 2, delay: 4.5, dur: 23, cls: 'hidden-sm' },
  { left: '6%', top: '50%', size: 3, delay: 3.5, dur: 25, cls: 'hidden-md' },
  { left: '45%', top: '8%', size: 2, delay: 0.5, dur: 17, cls: '' },
  { left: '30%', top: '15%', size: 3, delay: 5.5, dur: 27, cls: 'hidden-md' },
];

export default function Login() {
  const { signIn, user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<View>('menu');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [code, setCode] = useState('');
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user && !loading) {
      navigate(isAdmin ? '/' : '/pos', { replace: true });
    }
  }, [user, isAdmin, loading, navigate]);

  useEffect(() => {
    setFormError(null);
    if (view === 'cashier') {
      setTimeout(() => codeRef.current?.focus(), 50);
    }
  }, [view]);

  const handleAdminSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const email = String(formData.get('email') ?? '').trim();
    const password = String(formData.get('password') ?? '');

    if (!email || !password) {
      setFormError('Ingresa tu correo y contraseña.');
      setSubmitting(false);
      return;
    }

    const { error } = await signIn(email, password);
    setSubmitting(false);

    if (error) {
      setFormError(translateAdminError(error));
    }
  };

  const handleCashierSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    if (!/^\d{6}$/.test(code.trim())) {
      setFormError('Ingresa tu código de 6 dígitos.');
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('cashier_login', {
        body: { code: code.trim() },
      });

      if (error) {
        const ctx = (error as { context?: { json?: () => Promise<{ error?: string }> } }).context;
        let serverMsg: string | null = null;
        if (ctx?.json) {
          try {
            const j = await ctx.json();
            if (j?.error) serverMsg = j.error;
          } catch {
            // ignore
          }
        }
        setFormError(
          serverMsg
            ? mapCashierError(serverMsg)
            : 'No se pudo validar el acceso. Comprueba tu conexión e inténtalo nuevamente.'
        );
        setSubmitting(false);
        return;
      }

      if (data?.error) {
        setFormError(mapCashierError(data.error as string));
        setSubmitting(false);
        return;
      }

      const tokenHash = data?.token_hash as string | undefined;
      if (!tokenHash) {
        setFormError('No se pudo iniciar la sesión del cajero.');
        setSubmitting(false);
        return;
      }

      const { error: otpError } = await supabase.auth.verifyOtp({
        type: 'magiclink',
        token_hash: tokenHash,
      });

      if (otpError) {
        setFormError('No se pudo validar el acceso. Comprueba tu conexión e inténtalo nuevamente.');
        setSubmitting(false);
        return;
      }

      // El useEffect redirige al POS automáticamente.
    } catch {
      setFormError('No se pudo validar el acceso. Comprueba tu conexión e inténtalo nuevamente.');
      setSubmitting(false);
    }
  };

  const backToMenu = () => {
    setView('menu');
    setCode('');
  };

  return (
    <>
      <style>{`
        .login-page {
          position: relative;
          overflow: hidden;
          min-height: 100vh;
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 24px 16px;
          background: #05070c;
          background-image:
            radial-gradient(ellipse 80% 60% at 50% -10%, rgba(30, 64, 175, 0.28), transparent 60%),
            radial-gradient(ellipse 60% 50% at 100% 110%, rgba(30, 58, 138, 0.22), transparent 60%),
            radial-gradient(ellipse 50% 40% at 0% 100%, rgba(37, 99, 235, 0.16), transparent 60%);
        }

        .login-bg {
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
        }

        .bg-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(96, 165, 250, 0.045) 1px, transparent 1px),
            linear-gradient(90deg, rgba(96, 165, 250, 0.045) 1px, transparent 1px);
          background-size: 64px 64px;
          -webkit-mask-image: radial-gradient(circle at 50% 50%, transparent 28%, rgba(0, 0, 0, 0.7) 100%);
          mask-image: radial-gradient(circle at 50% 50%, transparent 28%, rgba(0, 0, 0, 0.7) 100%);
        }

        .bg-halo {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 560px;
          height: 560px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.16), rgba(30, 64, 175, 0.06) 45%, transparent 70%);
          filter: blur(40px);
          animation: bg-pulse 9s ease-in-out infinite;
        }

        .bg-glow {
          position: absolute;
          border-radius: 50%;
          filter: blur(90px);
          opacity: 0.55;
          animation: bg-drift 22s ease-in-out infinite;
        }

        .bg-glow-1 {
          width: 480px;
          height: 480px;
          left: -120px;
          top: -80px;
          background: radial-gradient(circle, rgba(37, 99, 235, 0.35), transparent 70%);
        }

        .bg-glow-2 {
          width: 520px;
          height: 520px;
          right: -140px;
          bottom: -120px;
          background: radial-gradient(circle, rgba(30, 64, 175, 0.3), transparent 70%);
          animation-delay: -11s;
        }

        .bg-glow-3 {
          width: 360px;
          height: 360px;
          left: -100px;
          bottom: 10%;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.2), transparent 70%);
          animation-delay: -6s;
        }

        .bg-line-h {
          position: absolute;
          width: 180px;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(96, 165, 250, 0.3), transparent);
        }

        .bg-line-v {
          position: absolute;
          width: 1px;
          height: 180px;
          background: linear-gradient(180deg, transparent, rgba(96, 165, 250, 0.3), transparent);
        }

        .bg-node {
          position: absolute;
          width: 5px;
          height: 5px;
          border-radius: 2px;
          background: rgba(147, 197, 253, 0.55);
          box-shadow: 0 0 8px rgba(96, 165, 250, 0.55);
        }

        .bg-node-twinkle {
          animation: bg-twinkle 5s ease-in-out infinite;
        }

        .bg-particle {
          position: absolute;
          border-radius: 50%;
          background: rgba(147, 197, 253, 0.75);
          box-shadow: 0 0 6px rgba(96, 165, 250, 0.6);
          animation: bg-float 18s ease-in-out infinite;
        }

        @keyframes bg-float {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.35; }
          50% { transform: translateY(-18px) translateX(6px); opacity: 0.9; }
        }

        @keyframes bg-drift {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(24px, -22px); }
        }

        @keyframes bg-pulse {
          0%, 100% { opacity: 0.75; }
          50% { opacity: 1; }
        }

        @keyframes bg-twinkle {
          0%, 100% { opacity: 0.35; }
          50% { opacity: 1; }
        }

        @media (max-width: 768px) {
          .hidden-md { display: none; }
          .bg-glow { opacity: 0.35; }
          .bg-grid { background-size: 72px 72px; }
        }

        @media (max-width: 480px) {
          .hidden-sm { display: none; }
          .bg-halo { width: 420px; height: 420px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .bg-particle,
          .bg-glow,
          .bg-halo,
          .bg-node-twinkle {
            animation: none !important;
          }
        }

        .glow-card {
          position: relative;
          width: 100%;
          max-width: 315px;
          border-radius: 24px;
          padding: 2px;
          overflow: hidden;
          box-shadow:
            0 4px 8px rgba(0,0,0,0.2),
            0 8px 16px rgba(0,0,0,0.2),
            0 0 8px rgba(255,255,255,0.1),
            0 0 16px rgba(255,255,255,0.08);
        }

        .glow-card::before {
          content: "";
          position: absolute;
          inset: -50px;
          background: conic-gradient(from 45deg, transparent 75%, #fff, transparent 100%);
          animation: login-spin 4s ease-in-out infinite;
        }

        @keyframes login-spin {
          100% { transform: rotate(360deg); }
        }

        .glass-card {
          position: relative;
          z-index: 10;
          background: #272727;
          border-radius: 22px;
          padding: 28px 24px;
          backdrop-filter: blur(15px);
          -webkit-backdrop-filter: blur(15px);
          box-shadow:
            inset 0 40px 60px -8px rgba(255,255,255,0.12),
            inset 4px 0 12px -6px rgba(255,255,255,0.12),
            inset 0 0 12px -4px rgba(255,255,255,0.12);
        }

        .login-logo {
          width: 76px;
          height: 76px;
          margin: 0 auto;
          display: block;
          object-fit: cover;
          transform: scale(1.08);
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.85);
          box-shadow:
            8px 8px 16px rgba(0,0,0,0.2),
            -8px -8px 16px rgba(255,255,255,0.06);
        }

        .login-title {
          color: #fff;
          font-size: 22px;
          font-weight: 700;
          line-height: 1.25;
          text-align: center;
        }

        .login-subtitle {
          color: rgba(255,255,255,0.55);
          font-size: 13px;
          text-align: center;
        }

        .login-label {
          display: block;
          color: rgba(255,255,255,0.6);
          font-size: 12px;
          font-weight: 500;
          margin-bottom: 6px;
        }

        .login-input {
          width: 100%;
          padding: 12px 14px;
          border: none;
          border-radius: 12px;
          background: #3a3a3a;
          color: #fff;
          outline: none;
          font-size: 14px;
        }

        .login-input::placeholder {
          color: rgba(255,255,255,0.35);
        }

        .login-input:focus {
          border: 1px solid #fff;
        }

        .login-code {
          text-align: center;
          font-size: 24px;
          font-weight: 700;
          letter-spacing: 0.5em;
          text-indent: 0.5em;
        }

        .login-button {
          width: 100%;
          height: 46px;
          border: none;
          border-radius: 23px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          background: #373737;
          color: #fff;
          white-space: nowrap;
          transition: 0.3s;
          box-shadow:
            inset 0px 3px 6px -4px rgba(255,255,255,0.6),
            inset 0px -3px 6px -2px rgba(0,0,0,0.8);
        }

        .login-button:hover {
          background: rgba(255,255,255,0.25);
          box-shadow:
            inset 0px 3px 6px rgba(255,255,255,0.6),
            inset 0px -3px 6px rgba(0,0,0,0.8),
            0px 0px 8px rgba(255,255,255,0.05);
        }

        .login-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .login-back {
          width: 100%;
          text-align: center;
          background: none;
          border: none;
          color: rgba(255,255,255,0.5);
          font-size: 13px;
          cursor: pointer;
          padding: 2px;
          transition: color 0.3s;
        }

        .login-back:hover {
          color: #fff;
        }

        .login-error {
          color: #ff7a7a;
          font-size: 12.5px;
          text-align: center;
        }

        .login-spinner {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          border: 2px solid rgba(255,255,255,0.35);
          border-top-color: #fff;
          animation: login-spin 0.8s linear infinite;
        }
      `}</style>

      <div className="login-page">
        <div className="login-bg" aria-hidden="true">
          <div className="bg-grid" />
          <div className="bg-halo" />
          <div className="bg-glow bg-glow-1" />
          <div className="bg-glow bg-glow-2" />
          <div className="bg-glow bg-glow-3" />

          <div className="bg-line-h" style={{ left: '4%', top: '14%' }} />
          <div className="bg-line-v" style={{ left: '4%', top: '14%' }} />
          <div className="bg-node" style={{ left: 'calc(4% + 180px)', top: 'calc(14% - 2px)' }} />
          <div className="bg-node" style={{ left: 'calc(4% - 2px)', top: 'calc(14% + 180px)' }} />

          <div className="bg-line-h" style={{ right: '5%', bottom: '16%' }} />
          <div className="bg-line-v" style={{ right: '5%', bottom: '16%' }} />
          <div className="bg-node" style={{ right: 'calc(5% + 180px)', bottom: 'calc(16% - 2px)' }} />
          <div className="bg-node" style={{ right: 'calc(5% - 2px)', bottom: 'calc(16% + 180px)' }} />

          <div className="bg-node bg-node-twinkle" style={{ right: '12%', top: '20%' }} />
          <div className="bg-node bg-node-twinkle" style={{ left: '12%', bottom: '20%' }} />
          <div className="bg-node" style={{ right: '8%', top: '48%' }} />
          <div className="bg-node" style={{ left: '8%', top: '44%' }} />

          {PARTICLES.map((p, i) => (
            <span
              key={i}
              className={`bg-particle ${p.cls}`}
              style={{
                left: p.left,
                top: p.top,
                width: p.size,
                height: p.size,
                animationDuration: `${p.dur}s`,
                animationDelay: `${p.delay}s`,
              }}
            />
          ))}
        </div>

        <div className="glow-card">
          <div className="glass-card">
            {view === 'menu' && (
              <div className="flex flex-col items-center gap-3">
                <img
                  className="login-logo"
                  src="https://storage.helloreaddy.io/project_files/0c9a9c3e-9011-4ee6-9dd2-90fcbe3bf101/f29088da-a32d-42f3-a831-1504283ab725_compressed_dk.webp"
                  alt="DK Supply"
                />

                <h1 className="login-title">
                  Sistema de Facturación
                  <span className="block">Simple</span>
                </h1>

                <p className="login-subtitle">Selecciona cómo deseas ingresar</p>

                <div className="w-full flex flex-col gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setView('cashier')}
                    className="login-button"
                  >
                    <i className="ri-terminal-box-line text-lg" />
                    Acceso a Caja
                  </button>
                  <button
                    type="button"
                    onClick={() => setView('admin')}
                    className="login-button"
                  >
                    <i className="ri-shield-user-line text-lg" />
                    Administrador
                  </button>
                </div>
              </div>
            )}

            {view === 'cashier' && (
              <form onSubmit={handleCashierSubmit} className="flex flex-col items-center gap-3" noValidate>
                <img
                  className="login-logo"
                  src="https://storage.helloreaddy.io/project_files/0c9a9c3e-9011-4ee6-9dd2-90fcbe3bf101/f29088da-a32d-42f3-a831-1504283ab725_compressed_dk.webp"
                  alt="DK Supply"
                />

                <h1 className="login-title">Acceso a Caja</h1>
                <p className="login-subtitle">Introduce tu código de cajero</p>

                <div className="w-full pt-1">
                  <label htmlFor="code" className="login-label">Código de cajero</label>
                  <input
                    id="code"
                    name="code"
                    ref={codeRef}
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="login-input login-code"
                  />
                </div>

                {formError && <p className="login-error">{formError}</p>}

                <button type="submit" disabled={submitting} className="login-button">
                  {submitting ? (
                    <>
                      <span className="login-spinner" />
                      Validando...
                    </>
                  ) : (
                    <>
                      <i className="ri-login-box-line text-lg" />
                      Ingresar
                    </>
                  )}
                </button>

                <button type="button" onClick={backToMenu} className="login-back">
                  ← Volver
                </button>
              </form>
            )}

            {view === 'admin' && (
              <form onSubmit={handleAdminSubmit} className="flex flex-col items-center gap-3" noValidate>
                <img
                  className="login-logo"
                  src="https://storage.helloreaddy.io/project_files/0c9a9c3e-9011-4ee6-9dd2-90fcbe3bf101/f29088da-a32d-42f3-a831-1504283ab725_compressed_dk.webp"
                  alt="DK Supply"
                />

                <h1 className="login-title">Acceso de Administrador</h1>

                <div className="w-full pt-1">
                  <label htmlFor="email" className="login-label">Correo electrónico</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="admin@negocio.com"
                    className="login-input"
                  />
                </div>

                <div className="w-full">
                  <label htmlFor="password" className="login-label">Contraseña</label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className="login-input"
                  />
                </div>

                {formError && <p className="login-error">{formError}</p>}

                <button type="submit" disabled={submitting} className="login-button">
                  {submitting ? (
                    <>
                      <span className="login-spinner" />
                      Ingresando...
                    </>
                  ) : (
                    <>
                      <i className="ri-shield-user-line text-lg" />
                      Ingresar como administrador
                    </>
                  )}
                </button>

                <button type="button" onClick={backToMenu} className="login-back">
                  ← Volver
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}